import type { CountryData } from 'react-international-phone';
import { buildCountryData, defaultCountries, parseCountry } from 'react-international-phone';
import { flagMapping } from './flagMapping';

export interface Country {
  name: string;
  iso2: string;
  dialCode: string;
  format: string;
  placeholder: string;
  flagSvg?: string;
}

export const countriesByIso2: Record<string, Country> = {
  af: { name: 'Afghanistan', iso2: 'af', dialCode: '93', format: '... ... ....', placeholder: '+93 XXX XXX XXXX' },
  ax: {
    name: 'Aland Islands',
    iso2: 'ax',
    dialCode: '358',
    format: '... ... ....',
    placeholder: '+358 XXX XXX XXXX',
  },
  al: { name: 'Albania', iso2: 'al', dialCode: '355', format: '... ... ....', placeholder: '+355 XXX XXX XXXX' },
  dz: { name: 'Algeria', iso2: 'dz', dialCode: '213', format: '... ... ....', placeholder: '+213 XXX XXX XXXX' },
  as: {
    name: 'American Samoa',
    iso2: 'as',
    dialCode: '1684',
    format: '... ....',
    placeholder: '+1 684 XXX XXXX',
  },
  ad: { name: 'Andorra', iso2: 'ad', dialCode: '376', format: '... ...', placeholder: '+376 XXX XXX' },
  ao: { name: 'Angola', iso2: 'ao', dialCode: '244', format: '... ... ...', placeholder: '+244 XXX XXX XXX' },
  ai: { name: 'Anguilla', iso2: 'ai', dialCode: '1264', format: '... ....', placeholder: '+1 264 XXX XXXX' },
  aq: { name: 'Antarctica', iso2: 'aq', dialCode: '672', format: '... ...', placeholder: '+672 XXX XXX' },
  ag: {
    name: 'Antigua and Barbuda',
    iso2: 'ag',
    dialCode: '1268',
    format: '... ....',
    placeholder: '+1 268 XXX XXXX',
  },
  ar: { name: 'Argentina', iso2: 'ar', dialCode: '54', format: '... ... ....', placeholder: '+54 XXX XXX XXXX' },
  am: { name: 'Armenia', iso2: 'am', dialCode: '374', format: '.. ......', placeholder: '+374 XX XXXXXX' },
  aw: { name: 'Aruba', iso2: 'aw', dialCode: '297', format: '... ....', placeholder: '+297 XXX XXXX' },
  au: { name: 'Australia', iso2: 'au', dialCode: '61', format: '. .... ....', placeholder: '+61 X XXXX XXXX' },
  at: { name: 'Austria', iso2: 'at', dialCode: '43', format: '... ......', placeholder: '+43 XXX XXXXXXX' },
  az: {
    name: 'Azerbaijan',
    iso2: 'az',
    dialCode: '994',
    format: '.. ... .. ..',
    placeholder: '+994 XX XXX XX XX',
  },
  bs: { name: 'Bahamas', iso2: 'bs', dialCode: '1242', format: '... ....', placeholder: '+1 242 XXX XXXX' },
  bh: { name: 'Bahrain', iso2: 'bh', dialCode: '973', format: '.... ....', placeholder: '+973 XXXX XXXX' },
  bd: { name: 'Bangladesh', iso2: 'bd', dialCode: '880', format: '....-......', placeholder: '+880 XXXX-XXXXXX' },
  bb: { name: 'Barbados', iso2: 'bb', dialCode: '1246', format: '... ....', placeholder: '+1 246 XXX XXXX' },
  by: { name: 'Belarus', iso2: 'by', dialCode: '375', format: '.. ...-..-..', placeholder: '+375 XX XXX-XX-XX' },
  be: { name: 'Belgium', iso2: 'be', dialCode: '32', format: '... .. .. ..', placeholder: '+32 XXX XX XX XX' },
  bz: { name: 'Belize', iso2: 'bz', dialCode: '501', format: '...-....', placeholder: '+501 XXX-XXXX' },
  bj: { name: 'Benin', iso2: 'bj', dialCode: '229', format: '.. .. .. ..', placeholder: '+229 XX XX XX XX' },
  bm: { name: 'Bermuda', iso2: 'bm', dialCode: '1441', format: '... ....', placeholder: '+1 441 XXX XXXX' },
  bt: { name: 'Bhutan', iso2: 'bt', dialCode: '975', format: '. ... ...', placeholder: '+975 X XXX XXX' },
  bo: { name: 'Bolivia', iso2: 'bo', dialCode: '591', format: '. ... ....', placeholder: '+591 X XXX XXXX' },
  ba: {
    name: 'Bosnia and Herzegovina',
    iso2: 'ba',
    dialCode: '387',
    format: '.. ...-...',
    placeholder: '+387 XX XXX-XXX',
  },
  bw: { name: 'Botswana', iso2: 'bw', dialCode: '267', format: '.. ... ...', placeholder: '+267 XX XXX XXX' },
  br: { name: 'Brazil', iso2: 'br', dialCode: '55', format: '.. .....-....', placeholder: '+55 XX XXXXX-XXXX' },
  io: {
    name: 'British Indian Ocean Territory',
    iso2: 'io',
    dialCode: '246',
    format: '... ....',
    placeholder: '+246 XXX XXXX',
  },
  bn: { name: 'Brunei Darussalam', iso2: 'bn', dialCode: '673', format: '... ....', placeholder: '+673 XXX XXXX' },
  bg: { name: 'Bulgaria', iso2: 'bg', dialCode: '359', format: '... ... ...', placeholder: '+359 XXX XXX XXX' },
  bf: {
    name: 'Burkina Faso',
    iso2: 'bf',
    dialCode: '226',
    format: '.. .. .. ..',
    placeholder: '+226 XX XX XX XX',
  },
  bi: { name: 'Burundi', iso2: 'bi', dialCode: '257', format: '.. .. .. ..', placeholder: '+257 XX XX XX XX' },
  kh: { name: 'Cambodia', iso2: 'kh', dialCode: '855', format: '.. ... ...', placeholder: '+855 XX XXX XXX' },
  cm: { name: 'Cameroon', iso2: 'cm', dialCode: '237', format: '.... ....', placeholder: '+237 XXXX XXXX' },
  us: {
    name: 'United States',
    iso2: 'us',
    dialCode: '1',
    format: '(...) ...-....',
    placeholder: '+1 (XXX) XXX-XXXX',
  },
  ca: { name: 'Canada', iso2: 'ca', dialCode: '1', format: '(...) ...-....', placeholder: '+1 (XXX) XXX-XXXX' },
  cv: { name: 'Cape Verde', iso2: 'cv', dialCode: '238', format: '... .. ..', placeholder: '+238 XXX XX XX' },
  ky: { name: 'Cayman Islands', iso2: 'ky', dialCode: '345', format: '... ....', placeholder: '+345 XXX XXXX' },
  cf: {
    name: 'Central African Republic',
    iso2: 'cf',
    dialCode: '236',
    format: '.. .. .. ..',
    placeholder: '+236 XX XX XX XX',
  },
  td: { name: 'Chad', iso2: 'td', dialCode: '235', format: '.. .. .. ..', placeholder: '+235 XX XX XX XX' },
  cl: { name: 'Chile', iso2: 'cl', dialCode: '56', format: '. .... ....', placeholder: '+56 X XXXX XXXX' },
  cn: { name: 'China', iso2: 'cn', dialCode: '86', format: '... .... ....', placeholder: '+86 XXX XXXX XXXX' },
  cx: {
    name: 'Christmas Island',
    iso2: 'cx',
    dialCode: '61',
    format: '. .... ....',
    placeholder: '+61 X XXXX XXXX',
  },
  cc: {
    name: 'Cocos (Keeling) Islands',
    iso2: 'cc',
    dialCode: '61',
    format: '. .... ....',
    placeholder: '+61 X XXXX XXXX',
  },
  co: { name: 'Colombia', iso2: 'co', dialCode: '57', format: '... ... ....', placeholder: '+57 XXX XXX XXXX' },
  km: { name: 'Comoros', iso2: 'km', dialCode: '269', format: '... .. ..', placeholder: '+269 XXX XX XX' },
  cg: { name: 'Congo', iso2: 'cg', dialCode: '242', format: '.. ... ....', placeholder: '+242 XX XXX XXXX' },
  cd: {
    name: 'Congo, Democratic Republic of the Congo',
    iso2: 'cd',
    dialCode: '243',
    format: '.. ... ....',
    placeholder: '+243 XX XXX XXXX',
  },
  ck: { name: 'Cook Islands', iso2: 'ck', dialCode: '682', format: '.. ...', placeholder: '+682 XX XXX' },
  cr: { name: 'Costa Rica', iso2: 'cr', dialCode: '506', format: '.... ....', placeholder: '+506 XXXX XXXX' },
  ci: {
    name: 'Cote d\'Ivoire',
    iso2: 'ci',
    dialCode: '225',
    format: '.. .. .. ..',
    placeholder: '+225 XX XX XX XX',
  },
  hr: { name: 'Croatia', iso2: 'hr', dialCode: '385', format: '.. ...-....', placeholder: '+385 XX XXX-XXXX' },
  cu: { name: 'Cuba', iso2: 'cu', dialCode: '53', format: '. ... ....', placeholder: '+53 X XXX XXXX' },
  cy: { name: 'Cyprus', iso2: 'cy', dialCode: '357', format: '.. ......', placeholder: '+357 XX XXXXXX' },
  cz: {
    name: 'Czech Republic',
    iso2: 'cz',
    dialCode: '420',
    format: '... ... ...',
    placeholder: '+420 XXX XXX XXX',
  },
  dk: { name: 'Denmark', iso2: 'dk', dialCode: '45', format: '.. .. .. ..', placeholder: '+45 XX XX XX XX' },
  dj: { name: 'Djibouti', iso2: 'dj', dialCode: '253', format: '.. .. .. ..', placeholder: '+253 XX XX XX XX' },
  dm: { name: 'Dominica', iso2: 'dm', dialCode: '1767', format: '... ....', placeholder: '+1 767 XXX XXXX' },
  do: {
    name: 'Dominican Republic',
    iso2: 'do',
    dialCode: '1809',
    format: '... ....',
    placeholder: '+1 849 XXX XXXX',
  },
  ec: { name: 'Ecuador', iso2: 'ec', dialCode: '593', format: '. ... ....', placeholder: '+593 X XXX XXXX' },
  eg: { name: 'Egypt', iso2: 'eg', dialCode: '20', format: '... ... ....', placeholder: '+20 XXX XXX XXXX' },
  sv: { name: 'El Salvador', iso2: 'sv', dialCode: '503', format: '.... ....', placeholder: '+503 XXXX XXXX' },
  gq: {
    name: 'Equatorial Guinea',
    iso2: 'gq',
    dialCode: '240',
    format: '... ... ...',
    placeholder: '+240 XXX XXX XXX',
  },
  er: { name: 'Eritrea', iso2: 'er', dialCode: '291', format: '. ... ...', placeholder: '+291 X XXX XXX' },
  ee: { name: 'Estonia', iso2: 'ee', dialCode: '372', format: '.... ....', placeholder: '+372 XXXX XXXX' },
  et: { name: 'Ethiopia', iso2: 'et', dialCode: '251', format: '.. ... ....', placeholder: '+251 XX XXX XXXX' },
  fk: { name: 'Falkland Islands', iso2: 'fk', dialCode: '500', format: '.....', placeholder: '+500 XXXXX' },
  fo: { name: 'Faroe Islands', iso2: 'fo', dialCode: '298', format: '......', placeholder: '+298 XXXXXX' },
  fj: { name: 'Fiji', iso2: 'fj', dialCode: '679', format: '... ....', placeholder: '+679 XXX XXXX' },
  fi: { name: 'Finland', iso2: 'fi', dialCode: '358', format: '.. ... ....', placeholder: '+358 XX XXX XXXX' },
  fr: { name: 'France', iso2: 'fr', dialCode: '33', format: '. .. .. .. ..', placeholder: '+33 X XX XX XX XX' },
  gf: { name: 'French Guiana', iso2: 'gf', dialCode: '594', format: '..... ....', placeholder: '+594 XXXXX XXXX' },
  pf: { name: 'French Polynesia', iso2: 'pf', dialCode: '689', format: '.. .. ..', placeholder: '+689 XX XX XX' },
  ga: { name: 'Gabon', iso2: 'ga', dialCode: '241', format: '. .. .. ..', placeholder: '+241 X XX XX XX' },
  gm: { name: 'Gambia', iso2: 'gm', dialCode: '220', format: '... .. ..', placeholder: '+220 XXX XX XX' },
  ge: { name: 'Georgia', iso2: 'ge', dialCode: '995', format: '... .. .. ..', placeholder: '+995 XXX XX XX XX' },
  de: { name: 'Germany', iso2: 'de', dialCode: '49', format: '... ........', placeholder: '+49 xxx xxxxxxxx' },
  gh: { name: 'Ghana', iso2: 'gh', dialCode: '233', format: '.. ... ....', placeholder: '+233 XX XXX XXXX' },
  gi: { name: 'Gibraltar', iso2: 'gi', dialCode: '350', format: '... .....', placeholder: '+350 XXX XXXXX' },
  gr: { name: 'Greece', iso2: 'gr', dialCode: '30', format: '... ... ....', placeholder: '+30 XXX XXX XXXX' },
  gl: { name: 'Greenland', iso2: 'gl', dialCode: '299', format: '.. .. ..', placeholder: '+299 XX XX XX' },
  gd: { name: 'Grenada', iso2: 'gd', dialCode: '1473', format: '... ....', placeholder: '+1 473 XXX XXXX' },
  gp: {
    name: 'Guadeloupe',
    iso2: 'gp',
    dialCode: '590',
    format: '... .. .. ..',
    placeholder: '+590 XXX XX XX XX',
  },
  gu: { name: 'Guam', iso2: 'gu', dialCode: '1671', format: '... ....', placeholder: '+1 671 XXX XXXX' },
  gt: { name: 'Guatemala', iso2: 'gt', dialCode: '502', format: '. ... ....', placeholder: '+502 X XXX XXXX' },
  gg: { name: 'Guernsey', iso2: 'gg', dialCode: '44', format: '.... ......', placeholder: '+44 XXXX XXXXXX' },
  gn: { name: 'Guinea', iso2: 'gn', dialCode: '224', format: '.. ... ...', placeholder: '+224 XX XXX XXX' },
  gw: { name: 'Guinea-Bissau', iso2: 'gw', dialCode: '245', format: '. ......', placeholder: '+245 X XXXXXX' },
  gy: { name: 'Guyana', iso2: 'gy', dialCode: '595', format: '... ....', placeholder: '+595 XXX XXXX' },
  ht: { name: 'Haiti', iso2: 'ht', dialCode: '509', format: '.. .. ....', placeholder: '+509 XX XX XXXX' },
  va: { name: 'Holy See', iso2: 'va', dialCode: '379', format: '.. .... ....', placeholder: '+379 XX XXXX XXXX' },
  hn: { name: 'Honduras', iso2: 'hn', dialCode: '504', format: '....-....', placeholder: '+504 XXXX-XXXX' },
  hk: { name: 'Hong Kong', iso2: 'hk', dialCode: '852', format: '.... ....', placeholder: '+852 XXXX XXXX' },
  hu: { name: 'Hungary', iso2: 'hu', dialCode: '36', format: '.. ... ....', placeholder: '+36 XX XXX XXXX' },
  is: { name: 'Iceland', iso2: 'is', dialCode: '354', format: '... ....', placeholder: '+354 XXX XXXX' },
  in: { name: 'India', iso2: 'in', dialCode: '91', format: '.....-.....', placeholder: '+91 XXXXX-XXXXX' },
  id: { name: 'Indonesia', iso2: 'id', dialCode: '62', format: '...-...-...', placeholder: '+62 XXX-XXX-XXX' },
  ir: { name: 'Iran', iso2: 'ir', dialCode: '98', format: '... ... ....', placeholder: '+98 XXX XXX XXXX' },
  iq: { name: 'Iraq', iso2: 'iq', dialCode: '964', format: '... ... ....', placeholder: '+964 XXX XXX XXXX' },
  ie: { name: 'Ireland', iso2: 'ie', dialCode: '353', format: '.. ... ....', placeholder: '+353 XX XXX XXXX' },
  im: { name: 'Isle of Man', iso2: 'im', dialCode: '44', format: '.... ......', placeholder: '+44 XXXX XXXXXX' },
  il: { name: 'Israel', iso2: 'il', dialCode: '972', format: '..-...-....', placeholder: '+972 XX-XXX-XXXX' },
  it: { name: 'Italy', iso2: 'it', dialCode: '39', format: '... ... ....', placeholder: '+39 XXX XXX XXXX' },
  jm: { name: 'Jamaica', iso2: 'jm', dialCode: '1876', format: '... ....', placeholder: '+1 876 XXX XXXX' },
  jp: { name: 'Japan', iso2: 'jp', dialCode: '81', format: '..-....-....', placeholder: '+81 XX-XXXX-XXXX' },
  je: { name: 'Jersey', iso2: 'je', dialCode: '44', format: '.... ......', placeholder: '+44 XXXX XXXXXX' },
  jo: { name: 'Jordan', iso2: 'jo', dialCode: '962', format: '. .... ....', placeholder: '+962 X XXXX XXXX' },
  kz: { name: 'Kazakhstan', iso2: 'kz', dialCode: '7', format: '... ... .. ..', placeholder: '+77 XXX XXX XX XX' },
  ke: { name: 'Kenya', iso2: 'ke', dialCode: '254', format: '... ......', placeholder: '+254 XXX XXXXXX' },
  ki: { name: 'Kiribati', iso2: 'ki', dialCode: '686', format: '.. ...', placeholder: '+686 XX XXX' },
  kp: {
    name: 'Korea, Democratic People’s Republic',
    iso2: 'kp',
    dialCode: '850',
    format: '... .... ....',
    placeholder: '+850 XXX XXXX XXXX',
  },
  kr: {
    name: 'Korea, Republic of',
    iso2: 'kr',
    dialCode: '82',
    format: '..-...-....',
    placeholder: '+82 XX-XXX-XXXX',
  },
  kw: { name: 'Kuwait', iso2: 'kw', dialCode: '965', format: '.... ....', placeholder: '+965 XXXX XXXX' },
  kg: { name: 'Kyrgyzstan', iso2: 'kg', dialCode: '996', format: '... ... ...', placeholder: '+996 XXX XXX XXX' },
  la: { name: 'Laos', iso2: 'la', dialCode: '856', format: '.. .. ... ...', placeholder: '+856 XX XX XXX XXX' },
  lv: { name: 'Latvia', iso2: 'lv', dialCode: '371', format: '.. ... ...', placeholder: '+371 XX XXX XXX' },
  lb: { name: 'Lebanon', iso2: 'lb', dialCode: '961', format: '. ... ...', placeholder: '+961 X XXX XXX' },
  ls: { name: 'Lesotho', iso2: 'ls', dialCode: '266', format: '. ... ....', placeholder: '+266 X XXX XXXX' },
  lr: { name: 'Liberia', iso2: 'lr', dialCode: '231', format: '.. ... ...', placeholder: '+231 XX XXX XXX' },
  ly: { name: 'Libya', iso2: 'ly', dialCode: '218', format: '..-......', placeholder: '+218 XX-XXXXXX' },
  li: { name: 'Liechtenstein', iso2: 'li', dialCode: '423', format: '... .. ..', placeholder: '+423 XXX XX XX' },
  lt: { name: 'Lithuania', iso2: 'lt', dialCode: '370', format: '... .. ...', placeholder: '+370 XXX XX XXX' },
  lu: { name: 'Luxembourg', iso2: 'lu', dialCode: '352', format: '... ...', placeholder: '+352 XXX XXX' },
  mo: { name: 'Macao', iso2: 'mo', dialCode: '853', format: '.... ....', placeholder: '+853 XXXX XXXX' },
  mk: { name: 'Macedonia', iso2: 'mk', dialCode: '389', format: '.. ... ...', placeholder: '+389 XX XXX XXX' },
  mg: {
    name: 'Madagascar',
    iso2: 'mg',
    dialCode: '261',
    format: '.. .. ... ..',
    placeholder: '+261 XX XX XXX XX',
  },
  mw: { name: 'Malawi', iso2: 'mw', dialCode: '265', format: '. .... ....', placeholder: '+265 X XXXX XXXX' },
  my: { name: 'Malaysia', iso2: 'my', dialCode: '60', format: '.-... ....', placeholder: '+60 X-XXX XXXX' },
  mv: { name: 'Maldives', iso2: 'mv', dialCode: '960', format: '...-....', placeholder: '+960 XXX-XXXX' },
  ml: { name: 'Mali', iso2: 'ml', dialCode: '223', format: '.. .. .. ..', placeholder: '+223 XX XX XX XX' },
  mt: { name: 'Malta', iso2: 'mt', dialCode: '356', format: '.... ....', placeholder: '+356 XXXX XXXX' },
  mh: { name: 'Marshall Islands', iso2: 'mh', dialCode: '692', format: '...-....', placeholder: '+692 XXX-XXXX' },
  mq: {
    name: 'Martinique',
    iso2: 'mq',
    dialCode: '596',
    format: '... .. .. ..',
    placeholder: '+596 XXX XX XX XX',
  },
  mr: { name: 'Mauritania', iso2: 'mr', dialCode: '222', format: '.. .. .. ..', placeholder: '+222 XX XX XX XX' },
  mu: { name: 'Mauritius', iso2: 'mu', dialCode: '230', format: '... ....', placeholder: '+230 XXX XXXX' },
  yt: { name: 'Mayotte', iso2: 'yt', dialCode: '262', format: '... .. .. ..', placeholder: '+262 XXX XX XX XX' },
  mx: { name: 'Mexico', iso2: 'mx', dialCode: '52', format: '... ... ....', placeholder: '+52 XXX XXX XXXX' },
  fm: {
    name: 'Micronesia',
    iso2: 'fm',
    dialCode: '691',
    format: '... ....',
    placeholder: '+691 XXX XXXX',
  },
  md: { name: 'Moldova', iso2: 'md', dialCode: '373', format: '.... ....', placeholder: '+373 XXXX XXXX' },
  mc: { name: 'Monaco', iso2: 'mc', dialCode: '377', format: '.. .. .. ..', placeholder: '+377 XX XX XX XX' },
  mn: { name: 'Mongolia', iso2: 'mn', dialCode: '976', format: '.. .. ....', placeholder: '+976 XX XX XXXX' },
  me: { name: 'Montenegro', iso2: 'me', dialCode: '382', format: '.. ... ...', placeholder: '+382 XX XXX XXX' },
  ms: { name: 'Montserrat', iso2: 'ms', dialCode: '1664', format: '... ....', placeholder: '+1 664 XXX XXXX' },
  ma: { name: 'Morocco', iso2: 'ma', dialCode: '212', format: '..-......', placeholder: '+212 XX-XXXXXX' },
  mz: { name: 'Mozambique', iso2: 'mz', dialCode: '258', format: '.. ... ...', placeholder: '+258 XX XXX XXX' },
  mm: { name: 'Myanmar', iso2: 'mm', dialCode: '95', format: '.. ... ...', placeholder: '+95 XX XXX XXX' },
  na: { name: 'Namibia', iso2: 'na', dialCode: '264', format: '... ......', placeholder: '+264 XXX XXXXXX' },
  nr: { name: 'Nauru', iso2: 'nr', dialCode: '674', format: '... ....', placeholder: '+674 XXX XXXX' },
  np: { name: 'Nepal', iso2: 'np', dialCode: '977', format: '...-.......', placeholder: '+977 XXX-XXXXXXX' },
  nl: { name: 'Netherlands', iso2: 'nl', dialCode: '31', format: '.. ... ....', placeholder: '+31 XX XXX XXXX' },
  nc: { name: 'New Caledonia', iso2: 'nc', dialCode: '687', format: '.. .. ..', placeholder: '+687 XX XX XX' },
  nz: { name: 'New Zealand', iso2: 'nz', dialCode: '64', format: '... ... ....', placeholder: '+64 XXX XXX XXXX' },
  ni: { name: 'Nicaragua', iso2: 'ni', dialCode: '505', format: '.... ....', placeholder: '+505 XXXX XXXX' },
  ne: { name: 'Niger', iso2: 'ne', dialCode: '227', format: '.. .. .. ..', placeholder: '+227 XX XX XX XX' },
  ng: { name: 'Nigeria', iso2: 'ng', dialCode: '234', format: '... ... ....', placeholder: '+234 XXX XXX XXXX' },
  nu: { name: 'Niue', iso2: 'nu', dialCode: '683', format: '....', placeholder: '+683 XXXX' },
  nf: { name: 'Norfolk Island', iso2: 'nf', dialCode: '672', format: '... ...', placeholder: '+672 XXX XXX' },
  mp: {
    name: 'Northern Mariana Islands',
    iso2: 'mp',
    dialCode: '1670',
    format: '... ....',
    placeholder: '+1 670 XXX XXXX',
  },
  no: { name: 'Norway', iso2: 'no', dialCode: '47', format: '... .. ...', placeholder: '+47 XXX XX XXX' },
  om: { name: 'Oman', iso2: 'om', dialCode: '968', format: '.... ....', placeholder: '+968 XXXX XXXX' },

  pk: { name: 'Pakistan', iso2: 'pk', dialCode: '92', format: '...-.......', placeholder: '+92 XXX-XXXXXXX' },
  pw: { name: 'Palau', iso2: 'pw', dialCode: '680', format: '... ....', placeholder: '+680 XXX XXXX' },
  ps: { name: 'Palestine', iso2: 'ps', dialCode: '970', format: '... ... ....', placeholder: '+970 XXX XXX XXXX' },
  pa: { name: 'Panama', iso2: 'pa', dialCode: '507', format: '...-....', placeholder: '+507 XXX-XXXX' },
  pg: { name: 'Papua New Guinea', iso2: 'pg', dialCode: '675', format: '... ....', placeholder: '+675 XXX XXXX' },
  py: { name: 'Paraguay', iso2: 'py', dialCode: '595', format: '... ... ...', placeholder: '+595 XXX XXX XXX' },
  pe: { name: 'Peru', iso2: 'pe', dialCode: '51', format: '... ... ...', placeholder: '+51 XXX XXX XXX' },
  ph: { name: 'Philippines', iso2: 'ph', dialCode: '63', format: '... ... ....', placeholder: '+63 XXX XXX XXXX' },
  pn: { name: 'Pitcairn', iso2: 'pn', dialCode: '870', format: '.... ... ...', placeholder: '+870 XXXX XXX XXX' },
  pl: { name: 'Poland', iso2: 'pl', dialCode: '48', format: '... ... ...', placeholder: '+48 XXX XXX XXX' },
  pt: { name: 'Portugal', iso2: 'pt', dialCode: '351', format: '... ... ...', placeholder: '+351 XXX XXX XXX' },
  pr: { name: 'Puerto Rico', iso2: 'pr', dialCode: '1787', format: '... ....', placeholder: '+1 787 XXX XXXX' },
  qa: { name: 'Qatar', iso2: 'qa', dialCode: '974', format: '.... ....', placeholder: '+974 XXXX XXXX' },

  re: { name: 'Reunion', iso2: 're', dialCode: '262', format: '... .. .. ..', placeholder: '+262 XXX XX XX XX' },
  ro: { name: 'Romania', iso2: 'ro', dialCode: '40', format: '... ... ...', placeholder: '+40 XXX XXX XXX' },
  ru: { name: 'Russia', iso2: 'ru', dialCode: '7', format: '... ... .. ..', placeholder: '+7 XXX XXX XX XX' },
  rw: { name: 'Rwanda', iso2: 'rw', dialCode: '250', format: '... ... ...', placeholder: '+250 XXX XXX XXX' },

  sh: { name: 'Saint Helena', iso2: 'sh', dialCode: '290', format: '....', placeholder: '+290 XXXX' },
  kn: {
    name: 'Saint Kitts and Nevis',
    iso2: 'kn',
    dialCode: '1869',
    format: '... ....',
    placeholder: '+1 869 XXX XXXX',
  },
  lc: { name: 'Saint Lucia', iso2: 'lc', dialCode: '1758', format: '... ....', placeholder: '+1 758 XXX XXXX' },
  pm: {
    name: 'Saint Pierre and Miquelon',
    iso2: 'pm',
    dialCode: '508',
    format: '... ....',
    placeholder: '+508 XXX XXXX',
  },
  vc: {
    name: 'Saint Vincent and the Grenadines',
    iso2: 'vc',
    dialCode: '1784',
    format: '... ....',
    placeholder: '+1 784 XXX XXXX',
  },

  ws: { name: 'Samoa', iso2: 'ws', dialCode: '685', format: '.....', placeholder: '+685 XXXXX' },
  sm: { name: 'San Marino', iso2: 'sm', dialCode: '378', format: '.... ......', placeholder: '+378 XXXX XXXXXX' },
  st: {
    name: 'Sao Tome and Principe',
    iso2: 'st',
    dialCode: '239',
    format: '.. ... ....',
    placeholder: '+239 XX XXX XXXX',
  },
  sa: { name: 'Saudi Arabia', iso2: 'sa', dialCode: '966', format: '... ... ....', placeholder: '+966 XXX XXX XXXX' },
  sn: { name: 'Senegal', iso2: 'sn', dialCode: '221', format: '.. ... ....', placeholder: '+221 XX XXX XXXX' },
  rs: { name: 'Serbia', iso2: 'rs', dialCode: '381', format: '.. ... ....', placeholder: '+381 XX XXX XXXX' },
  sc: { name: 'Seychelles', iso2: 'sc', dialCode: '248', format: '. .. .. ..', placeholder: '+248 X XX XX XX' },
  sl: { name: 'Sierra Leone', iso2: 'sl', dialCode: '232', format: '.. ......', placeholder: '+232 XX XXXXXX' },
  sg: { name: 'Singapore', iso2: 'sg', dialCode: '65', format: '.... ....', placeholder: '+65 XXXX XXXX' },
  sk: { name: 'Slovakia', iso2: 'sk', dialCode: '421', format: '... ... ...', placeholder: '+421 XXX XXX XXX' },
  si: { name: 'Slovenia', iso2: 'si', dialCode: '386', format: '.. ... ...', placeholder: '+386 XX XXX XXX' },
  sb: { name: 'Solomon Islands', iso2: 'sb', dialCode: '677', format: '... ....', placeholder: '+677 XXX XXXX' },
  so: { name: 'Somalia', iso2: 'so', dialCode: '252', format: '.. ... ...', placeholder: '+252 XX XXX XXX' },
  za: { name: 'South Africa', iso2: 'za', dialCode: '27', format: '... ... ....', placeholder: '+27 XXX XXX XXXX' },
  gs: {
    name: 'South Georgia and Sandwich Isl.',
    iso2: 'gs',
    dialCode: '500',
    format: '.....',
    placeholder: '+500 XXXXX',
  },
  ss: { name: 'South Sudan', iso2: 'ss', dialCode: '211', format: '.. ... ....', placeholder: '+211 XX XXX XXXX' },
  es: { name: 'Spain', iso2: 'es', dialCode: '34', format: '... ... ...', placeholder: '+34 XXX XXX XXX' },
  lk: { name: 'Sri Lanka', iso2: 'lk', dialCode: '94', format: '.. ... ....', placeholder: '+94 XX XXX XXXX' },
  sd: { name: 'Sudan', iso2: 'sd', dialCode: '249', format: '.. ... ....', placeholder: '+249 XX XXX XXXX' },
  sr: { name: 'Suriname', iso2: 'sr', dialCode: '597', format: '... ....', placeholder: '+597 XXX XXXX' },
  sj: {
    name: 'Svalbard and Jan Mayen',
    iso2: 'sj',
    dialCode: '47',
    format: '... .. ...',
    placeholder: '+47 XXX XX XXX',
  },
  sz: { name: 'Swaziland', iso2: 'sz', dialCode: '268', format: '.... ....', placeholder: '+268 XXXX XXXX' },
  se: { name: 'Sweden', iso2: 'se', dialCode: '46', format: '... .. .. ..', placeholder: '+46 XXX XX XX XX' },
  ch: { name: 'Switzerland', iso2: 'ch', dialCode: '41', format: '.. ... .. ..', placeholder: '+41 XX XXX XX XX' },
  sy: { name: 'Syria', iso2: 'sy', dialCode: '963', format: '... .... ....', placeholder: '+963 XXX XXXX XXXX' },

  tw: { name: 'Taiwan', iso2: 'tw', dialCode: '886', format: '.... ....', placeholder: '+886 XXXX XXXX' },
  tj: { name: 'Tajikistan', iso2: 'tj', dialCode: '992', format: '... .. ....', placeholder: '+992 XXX XX XXXX' },
  tz: { name: 'Tanzania', iso2: 'tz', dialCode: '255', format: '... ... ...', placeholder: '+255 XXX XXX XXX' },
  th: { name: 'Thailand', iso2: 'th', dialCode: '66', format: '... ... ....', placeholder: '+66 XXX XXX XXXX' },
  tl: { name: 'Timor-Leste', iso2: 'tl', dialCode: '670', format: '... ....', placeholder: '+670 XXX XXXX' },
  tg: { name: 'Togo', iso2: 'tg', dialCode: '228', format: '.. .. .. ..', placeholder: '+228 XX XX XX XX' },
  tk: { name: 'Tokelau', iso2: 'tk', dialCode: '690', format: '.....', placeholder: '+690 XXXXX' },
  to: { name: 'Tonga', iso2: 'to', dialCode: '676', format: '.....', placeholder: '+676 XXXXX' },
  tt: { name: 'Trinidad and Tobago', iso2: 'tt', dialCode: '1868', format: '... ....', placeholder: '+1 868 XXX XXXX' },
  tn: { name: 'Tunisia', iso2: 'tn', dialCode: '216', format: '.. ... ...', placeholder: '+216 XX XXX XXX' },
  tr: { name: 'Turkey', iso2: 'tr', dialCode: '90', format: '... ... ....', placeholder: '+90 XXX XXX XXXX' },
  tm: { name: 'Turkmenistan', iso2: 'tm', dialCode: '993', format: '.. ... ....', placeholder: '+993 XX XXX XXXX' },
  tc: {
    name: 'Turks and Caicos Islands',
    iso2: 'tc',
    dialCode: '1649',
    format: '... ....',
    placeholder: '+1 649 XXX XXXX',
  },
  tv: { name: 'Tuvalu', iso2: 'tv', dialCode: '688', format: '.....', placeholder: '+688 XXXXX' },

  ug: { name: 'Uganda', iso2: 'ug', dialCode: '256', format: '... ... ...', placeholder: '+256 XXX XXX XXX' },
  ua: { name: 'Ukraine', iso2: 'ua', dialCode: '380', format: '.. ... .. ..', placeholder: '+380 XX XXX XX XX' },
  ae: {
    name: 'United Arab Emirates',
    iso2: 'ae',
    dialCode: '971',
    format: '... ... ....',
    placeholder: '+971 XXX XXX XXXX',
  },
  gb: { name: 'United Kingdom', iso2: 'gb', dialCode: '44', format: '.... ......', placeholder: '+44 XXXX XXXXXX' },
  uy: { name: 'Uruguay', iso2: 'uy', dialCode: '598', format: '... ... ...', placeholder: '+598 XXX XXX XXX' },
  uz: { name: 'Uzbekistan', iso2: 'uz', dialCode: '998', format: '.. ... .. ..', placeholder: '+998 XX XXX XX XX' },

  vu: { name: 'Vanuatu', iso2: 'vu', dialCode: '678', format: '.... ....', placeholder: '+678 XXXX XXXX' },
  ve: { name: 'Venezuela', iso2: 've', dialCode: '58', format: '...-.......', placeholder: '+58 XXX-XXXXXXX' },
  vn: { name: 'Vietnam', iso2: 'vn', dialCode: '84', format: '... ... ....', placeholder: '+84 XXX XXX XXXX' },
  vg: {
    name: 'Virgin Islands, British',
    iso2: 'vg',
    dialCode: '1284',
    format: '... ....',
    placeholder: '+1 284 XXX XXXX',
  },
  vi: {
    name: 'Virgin Islands, U.S.',
    iso2: 'vi',
    dialCode: '1340',
    format: '... ....',
    placeholder: '+1 340 XXX XXXX',
  },

  wf: { name: 'Wallis and Futuna', iso2: 'wf', dialCode: '681', format: '.. .. ..', placeholder: '+681 XX XX XX' },
  eh: { name: 'Western Sahara', iso2: 'eh', dialCode: '212', format: '..-......', placeholder: '+212 XX-XXXXXX' },

  ye: { name: 'Yemen', iso2: 'ye', dialCode: '967', format: '.. ... ....', placeholder: '+967 XX XXX XXXX' },

  zm: { name: 'Zambia', iso2: 'zm', dialCode: '260', format: '... ... ...', placeholder: '+260 XXX XXX XXX' },
  zw: { name: 'Zimbabwe', iso2: 'zw', dialCode: '263', format: '.. ... ....', placeholder: '+263 XX XXX XXXX' },
};

export const getPlaceholderCountry = (iso2: string): Country | undefined => {
  return Object.values(countriesByIso2).find((country) => country.iso2 === iso2);
};

const addFlagSvgToCountries = (countriesList: Country[]): Country[] => {
  return countriesList.map((country) => ({
    ...country,
    flagSvg: flagMapping[country.iso2] ? `/icons/flags/${flagMapping[country.iso2]}` : undefined,
  }));
};

export const countriesWithSvg = addFlagSvgToCountries(Object.values(countriesByIso2));

export const getCountryByCode = (iso2: string): Country | undefined => {
  return countriesWithSvg.find((country) => country.iso2 === iso2);
};

export const updatedCountries = defaultCountries.map((country) => {
  const parsedCountry = parseCountry(country);
  const customCountry = countriesByIso2[parsedCountry.iso2];
  parsedCountry.format = customCountry?.format ?? parsedCountry.format;

  return buildCountryData(parsedCountry);
});

const iso2FromCountryData = (country: CountryData) => String(country[1]).toLowerCase();

export const orderCountriesByIso2Priority = (countries: CountryData[], priorityIso2: string[]): CountryData[] => {
  if (!priorityIso2.length) return countries;

  const byIso = new Map<string, CountryData>();
  for (const c of countries) {
    byIso.set(iso2FromCountryData(c), c);
  }

  const seen = new Set<string>();
  const result: CountryData[] = [];

  for (const raw of priorityIso2) {
    const iso = raw.trim().toLowerCase();
    if (!iso) continue;
    const c = byIso.get(iso);
    if (c && !seen.has(iso)) {
      result.push(c);
      seen.add(iso);
    }
  }

  for (const c of countries) {
    const iso = iso2FromCountryData(c);
    if (!seen.has(iso)) {
      result.push(c);
      seen.add(iso);
    }
  }

  return result;
};

export const orderPhoneCountryOptions = (
  countries: CountryData[],
  priorityIso2: string[],
  selectedIso2: string
): CountryData[] => {
  const byIso = new Map<string, CountryData>();
  for (const c of countries) {
    byIso.set(iso2FromCountryData(c), c);
  }

  const seen = new Set<string>();
  const result: CountryData[] = [];

  const selected = selectedIso2.trim().toLowerCase();
  if (selected) {
    const c = byIso.get(selected);
    if (c) {
      result.push(c);
      seen.add(selected);
    }
  }

  for (const raw of priorityIso2) {
    const iso = raw.trim().toLowerCase();
    if (!iso || seen.has(iso)) continue;
    const c = byIso.get(iso);
    if (c) {
      result.push(c);
      seen.add(iso);
    }
  }

  for (const c of countries) {
    const iso = iso2FromCountryData(c);
    if (!seen.has(iso)) {
      result.push(c);
      seen.add(iso);
    }
  }

  return result;
};
